import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache } from '@apollo/client'
import { Provider as ReduxProvider } from 'react-redux'
import { configureStore } from '@reduxjs/toolkit'
import type { PropsWithChildren } from 'react'
import type { StorefrontConfig, Tokens } from '@commerce/tokens'
import { cartSlice, catalogSlice, configSlice } from '@commerce/core'
import { StorefrontConfigProvider } from '../config/StorefrontConfigContext'
import { DemoProvider } from '../demo/DemoContext'
import { ThemeProvider } from './ThemeProvider'

/**
 * A cart's lists are replaced, never merged.
 *
 * A cart line has no `id`, so Apollo cannot normalise it and cannot tell whether
 * an incoming `items` array continues the cached one or supersedes it. Its
 * default is to warn ("Cache data may be lost when replacing the items field")
 * on every mutation result, which is both noise and a real ambiguity: for this
 * API the server's array *is* the cart, so replacing is correct and saying so
 * removes the guesswork.
 */
const cartLists = ['items', 'groups', 'activePromotions', 'deferredPromotions', 'activeFees']

/**
 * Every GraphQL operation POSTs to the same endpoint, so the network tab shows
 * a wall of identical `storefronts` requests. The operation name goes into the
 * query string — `…?op=CreateSession` — purely so a human can tell them apart;
 * the server ignores it and the request body is unchanged.
 */
const graphqlUri = (operationName: string | undefined): string => {
  const base: string = import.meta.env.VITE_GRAPHQL_URL
  // An anonymous operation has nothing to say; the bare endpoint is honest.
  if (operationName === undefined || operationName === '') return base
  return `${base}?op=${encodeURIComponent(operationName)}`
}

const apollo = new ApolloClient({
  link: new HttpLink({ uri: (operation) => graphqlUri(operation.operationName) }),
  cache: new InMemoryCache({
    typePolicies: {
      Cart: {
        fields: Object.fromEntries(
          cartLists.map((field) => [field, { merge: (_old: unknown, incoming: unknown) => incoming }]),
        ),
      },
    },
  }),
})

/**
 * Reducer keys are `catalog`, `cart` and `config`. `core`'s
 * `state/__fixtures__/preloadedState.ts` is keyed to match, so a test can pass
 * it straight to `configureStore({ preloadedState })`.
 *
 * Exposed as a factory as well as a singleton: the app wants one store, and a
 * test wants a fresh one per render so state cannot leak between cases.
 */
export const createAppStore = () =>
  configureStore({
    reducer: {
      catalog: catalogSlice.reducer,
      cart: cartSlice.reducer,
      config: configSlice.reducer,
    },
  })

export const store = createAppStore()

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

export const AppProviders = ({
  tokens,
  config,
  children,
}: PropsWithChildren<{ tokens: Tokens; config: StorefrontConfig }>) => (
  <ReduxProvider store={store}>
    <ApolloProvider client={apollo}>
      <StorefrontConfigProvider config={config}>
        <ThemeProvider tokens={tokens}>
          <DemoProvider>{children}</DemoProvider>
        </ThemeProvider>
      </StorefrontConfigProvider>
    </ApolloProvider>
  </ReduxProvider>
)
